import { yupResolver } from "@hookform/resolvers/yup";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { isEmpty } from "lodash";
import { useState } from "react";
import { useForm } from "react-hook-form";
import {
  SELECTED_CITY,
  SELECTED_COUNTRY,
  SELECTED_STATE,
} from "store/slices/appSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import { GET_COMPANY_BY_ID } from "utils/apiRoutes";
import axiosInstance from "utils/axiosInstance";
import { handleCreateFormData } from "utils/formHelpers";
import { refetchQuery } from "utils/helpers";
import { QueryKeys } from "utils/queryKey";
import { panRegex } from "utils/validationSchema";
import * as Yup from "yup";

export const useCompanyInformation = () => {
  const dispatch = useAppDispatch();

  const userInfo = useAppSelector((state) => state?.user?.userData);
  const countryList = useAppSelector((state) => state?.app?.countryList);
  const stateList = useAppSelector((state) => state?.app?.stateList);
  const cityList = useAppSelector((state) => state?.app?.cityList);

  const companyID = userInfo?.companyId;

  const [croppedImage, setCroppedImage] = useState<string | null>(null);

  const phoneValidation = Yup.object({
    phoneNumber: Yup.string().required("Please enter phone number"),
    name: Yup.string()
      .required("Please enter company name")
      .test(
        "whitespace-only",
        "Name cannot be empty or whitespace only",
        (value) => {
          return typeof value === "string" && value.trim().length > 0;
        },
      ),
    // gst: Yup.string().required("Please enter GSTN"),
    // sebiNumber: Yup.string().required("Please enter SEBI registration number"),
    panCard: Yup.string()
      .matches(panRegex, "PAN should have this format ABCDE1234A")
      .min(10, "Please enter valid pan")
      .max(10, "Please enter valid pan"),
    address: Yup.string().required("Please enter address"),
    pincode: Yup.string().required("Please enter pincode"),
    city: Yup.string().required("Please enter city"),
    state: Yup.string().required("Please enter state"),
    country: Yup.string().required("Please enter country"),
    pan: Yup.string()
      .matches(panRegex, "PAN should have this format ABCDE1234A")
      .min(10, "PAN should have 10 character")
      .max(10, "PAN should have 10 character"),
  });

  const { mutate: handleUpdate } = useMutation({
    mutationKey: ["UPDATE_COMPANY"],
    mutationFn: (data: any) =>
      axiosInstance.patch(`/company/${companyID}/`, data),
    onSuccess: () => {
      refetchQuery(QueryKeys.COMPANY_INFO);
      refetchQuery(QueryKeys.GET_USER_DETAIL);
    },
  });

  const { data: companyData, isFetching: companyFetching } = useFetchQuery({
    key: [QueryKeys.COMPANY_INFO, companyID],
    route: GET_COMPANY_BY_ID(companyID),
    enabled: Boolean(companyID),
    success: (response: any) => {
      setCroppedImage(response?.data?.logo);
      if (!isEmpty(response?.data?.country)) {
        dispatch(
          SELECTED_COUNTRY(
            countryList.find(
              (dataItem) => dataItem.name === response?.data?.country,
            )?.id,
          ),
        );
      }

      if (!isEmpty(response?.data?.state)) {
        dispatch(
          SELECTED_STATE(
            stateList.find(
              (dataItem) => dataItem.name === response?.data?.state,
            )?.id,
          ),
        );
      }

      if (!isEmpty(response?.data?.city)) {
        dispatch(
          SELECTED_CITY(
            cityList.find((dataItem) => dataItem.name === response?.data?.city)
              ?.id,
          ),
        );
      }
    },
  });

  const form = useForm<any>({
    defaultValues: {
      accountName: "",
      accountNumber: "",
      accountType: "",
      address1: "",
      address: "",
      address2: "",
      address3: "",
      bank: "",
      city: "",
      corrAddress1: "",
      corrAddress: "",
      corrAddress2: "",
      corrAddress3: "",
      corrCity: "",
      corrCountry: "",
      corrPincode: "",
      corrState: "",
      country: "",
      facebook: "",
      gst: "",
      instagram: "",
      internal: false,
      isfcCode: "",
      licenseNo: "",
      linkedin: "",
      name: "",
      panCard: "",
      phoneNumber: "",
      pincode: "",
      primaryContactEmail: "",
      primaryContactName: "",
      primaryContactNumber: "",
      sameBillingChecked: false,
      sebiNumber: "",
      secondaryContactEmail: "",
      secondaryContactName: "",
      secondaryContactNumber: "",
      state: "",
      twitter: "",
      website: "",
      whatsappNumber: "",
      countryCode: "+91",
      secondaryCountryCode: "+91",
      whatsappCountryCode: "+91",
      primaryCountryCode: "+91",
      youtube: "",
      logo: [],
      ckycId: "",
      isSamePermanentAddress: false,
    },
    values: companyData?.data,
    resolver: yupResolver(phoneValidation),
  });

  const onSubmit = (values: any) => {
    handleUpdate(
      handleCreateFormData({
        values: values,
        dirtyFields: form.formState.dirtyFields,
      }),
    );
  };

  return {
    companyFetching,
    form,
    onSubmit,
    handleUpdate,
    croppedImage,
    setCroppedImage,
  };
};
